import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSuperAdminStore } from '../store/useSuperAdminStore';
import type { AdminDataTableFilterValues, AdminDataTableSortState } from './components';
import { useAdminToast } from './components';
import { getAdminApiError } from '../lib/adminApiErrors';
import {
  parseLicenseTableQuery,
  serializeLicenseTableQuery,
  type LicenseTableFilters,
  type LicenseTableQueryState,
} from '../lib/adminLicenses';
import LicenseDrawer from './LicenseDrawer';
import LicenseTable from './LicenseTable';
import { AdminRouteErrorStack } from './AdminShell';

type OpenDrawer = { mode: 'create' } | { mode: 'edit'; licenseId: number };

const QueryKeys = ['key', 'company', 'type', 'status', 'sort'];

const normalizeFilters = (filters: AdminDataTableFilterValues): LicenseTableFilters => ({
  activationKey: filters.activationKey ?? '',
  company: filters.company ?? '',
  type: filters.type ?? '',
  status: filters.status ?? '',
});

const useLicenseRouteStore = () => ({
  licenses: useSuperAdminStore((state) => state.licenses),
  companies: useSuperAdminStore((state) => state.companies),
  companyLoading: useSuperAdminStore((state) => state.companyLoading),
  companyError: useSuperAdminStore((state) => state.companyError),
  licenseDetail: useSuperAdminStore((state) => state.licenseDetail),
  licenseLoading: useSuperAdminStore((state) => state.licenseLoading),
  licenseError: useSuperAdminStore((state) => state.licenseError),
  fetchLicenses: useSuperAdminStore((state) => state.fetchLicenses),
  fetchCompanies: useSuperAdminStore((state) => state.fetchCompanies),
  loadLicenseDetail: useSuperAdminStore((state) => state.loadLicenseDetail),
  clearLicenseDetail: useSuperAdminStore((state) => state.clearLicenseDetail),
  createLicense: useSuperAdminStore((state) => state.createLicense),
  saveLicense: useSuperAdminStore((state) => state.saveLicense),
  deleteLicense: useSuperAdminStore((state) => state.deleteLicense),
  bulkLicenses: useSuperAdminStore((state) => state.bulkLicenses),
});

const LicenseRouteErrors: React.FC<{
  companyError: string | null;
  licenseError: ReturnType<typeof useLicenseRouteStore>['licenseError'];
  showCompanyError: boolean;
  showLicenseError: boolean;
}> = ({ companyError, licenseError, showCompanyError, showLicenseError }) => (
  <>
    {showLicenseError && licenseError && (
      <div className="admin-licenses-error" role="alert">
        Unable to load licenses: {licenseError.message}
      </div>
    )}
    {showCompanyError && companyError && (
      <div className="admin-licenses-error" role="alert">
        Unable to load companies: {companyError}
      </div>
    )}
  </>
);

const AdminLicensesRoute: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useAdminToast();
  const store = useLicenseRouteStore();
  const { fetchCompanies, fetchLicenses, licenses } = store;
  const [drawer, setDrawer] = React.useState<OpenDrawer | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const queryState = React.useMemo(
    () => parseLicenseTableQuery(searchParams),
    [searchParams]
  );
  const companyIdFilter = React.useMemo(() => {
    const value = Number(searchParams.get('companyId'));
    return Number.isInteger(value) && value > 0 ? value : null;
  }, [searchParams]);
  const visibleLicenses = React.useMemo(() => companyIdFilter === null
    ? licenses
    : licenses.filter((license) => license.companyId === companyIdFilter), [companyIdFilter, licenses]);

  React.useEffect(() => {
    void Promise.all([fetchLicenses(), fetchCompanies()]);
  }, [fetchCompanies, fetchLicenses]);

  React.useEffect(() => {
    const availableIds = new Set(visibleLicenses.map((license) => license.id));
    setSelectedIds((current) => current.filter((id) => availableIds.has(id)));
  }, [visibleLicenses]);

  const writeQuery = (state: LicenseTableQueryState) => {
    const next = new URLSearchParams(searchParams);
    QueryKeys.forEach((key) => next.delete(key));
    serializeLicenseTableQuery(state).forEach((value, key) => next.set(key, value));
    setSearchParams(next, { replace: true });
  };

  const openEdit = async (licenseId: number) => {
    setDrawer({ mode: 'edit', licenseId });
    try {
      await store.loadLicenseDetail(licenseId);
    } catch (error) {
      const failure = getAdminApiError(error, 'license_detail_failed');
      showToast(`License details failed to load (${failure.code}).`, { tone: 'critical' });
      setDrawer(null);
      store.clearLicenseDetail();
    }
  };

  const closeDrawer = () => {
    setDrawer(null);
    store.clearLicenseDetail();
  };

  const activeDetail = drawer?.mode === 'edit'
    && store.licenseDetail?.id === drawer.licenseId
    ? store.licenseDetail
    : null;
  const showCompanyError = store.companies.length === 0;
  const showLicenseError = store.licenses.length === 0 && !drawer;
  const hasRouteError = Boolean(
    (showCompanyError && store.companyError) || (showLicenseError && store.licenseError)
  );

  return (
    <>
      <AdminRouteErrorStack active={hasRouteError}>
        <LicenseRouteErrors
          companyError={store.companyError}
          licenseError={store.licenseError}
          showCompanyError={showCompanyError}
          showLicenseError={showLicenseError}
        />
        <LicenseTable
          licenses={visibleLicenses}
          onAdd={() => setDrawer({ mode: 'create' })}
          addDisabled={store.companyLoading || store.companies.length === 0}
          onOpen={(license) => void openEdit(license.id)}
          onBulkAction={(action, ids) => store.bulkLicenses(
            action,
            ids,
            action === 'extend' ? 12 : undefined
          )}
          filterValues={queryState.filters}
          onFilterValuesChange={(filters) => writeQuery({
            filters: normalizeFilters(filters),
            sort: queryState.sort,
          })}
          sort={queryState.sort as AdminDataTableSortState}
          onSortChange={(sort) => writeQuery({ filters: queryState.filters, sort })}
          selectedIds={selectedIds}
          onSelectedIdsChange={(ids) => setSelectedIds(
            ids.map(Number).filter(Number.isFinite)
          )}
          busy={store.licenseLoading || store.companyLoading}
        />
      </AdminRouteErrorStack>
      {drawer && (
        <LicenseDrawer
          open
          mode={drawer.mode}
          license={activeDetail}
          companies={store.companies}
          loading={drawer.mode === 'edit' && store.licenseLoading && activeDetail === null}
          onClose={closeDrawer}
          onCreate={store.createLicense}
          onSave={store.saveLicense}
          onDelete={store.deleteLicense}
        />
      )}
    </>
  );
};

export default AdminLicensesRoute;
